import { clamp } from "./ShareCardLayout";
import { constrainImageElement } from "./ShareCardElements";

const ASSET_ROOT = "/images/share-editor/tooltip";
export const TOOLTIP_TITLE_FONT = '"ShareTooltipTitle", Georgia, serif';
export const TOOLTIP_BODY_FONT = '"ShareTooltipBody", Arial, sans-serif';
export const TOOLTIP_SAMPLE = { graphic: "tooltip", title: "Battlecry", text: "Does something when you play it from your hand.", layoutWidth: 260 };
let assetPromise;

export const loadTooltipAssets = () => {
  if (!assetPromise) {
    const frame = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not load the tooltip frame. Please try again."));
      image.src = `${ASSET_ROOT}/tooltip-frame.png`;
    });
    const fonts = [["ShareTooltipTitle", "Belwe_Outline.ttf"], ["ShareTooltipBody", "FranklinGothic.ttf"]].map(async ([family, file]) => {
      const face = await new FontFace(family, `url("${ASSET_ROOT}/${file}")`).load();
      document.fonts.add(face);
    });
    assetPromise = Promise.all([frame, ...fonts]).then(([image]) => ({ frame: image })).catch((error) => {
      assetPromise = null;
      throw error;
    });
  }
  return assetPromise;
};

const wrapText = (context, value, width) => {
  const lines = [];
  String(value).replace(/\r\n?/g, "\n").split("\n").forEach((paragraph) => {
    let line = "";
    const words = paragraph.trim().split(/\s+/);
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (context.measureText(candidate).width <= width) { line = candidate; return; }
      if (line) { lines.push(line); line = ""; }
      for (const character of Array.from(word)) {
        if (line && context.measureText(line + character).width > width) { lines.push(line); line = ""; }
        line += character;
      }
    });
    lines.push(line);
  });
  return lines;
};

export const getTooltipLayout = (element) => {
  const width = clamp(Number(element.layoutWidth) || 260, 180, 520);
  const padding = 17;
  const context = document.createElement("canvas").getContext("2d");
  const title = String(element.title || "").slice(0, 160);
  const text = String(element.text || "").slice(0, 3000);
  const titleSize = 24, bodySize = 22, titleAdvance = 29, bodyAdvance = 28;
  context.font = `400 ${titleSize}px ${TOOLTIP_TITLE_FONT}`;
  const titleLines = title ? wrapText(context, title, width - padding * 2 - 3) : [];
  context.font = `400 ${bodySize}px ${TOOLTIP_BODY_FONT}`;
  const bodyLines = text ? wrapText(context, text, width - padding * 2) : [];
  const titleY = 17;
  const bodyY = titleY + titleLines.length * titleAdvance + (titleLines.length && bodyLines.length ? 8 : 0);
  const height = Math.max(70, bodyY + bodyLines.length * bodyAdvance + 22);
  return { width, height, padding, titleY, bodyY, titleSize, bodySize, titleAdvance, bodyAdvance, titleLines, bodyLines };
};

export const createTooltipElement = (scene, topZ) => {
  const layout = getTooltipLayout(TOOLTIP_SAMPLE);
  const width = Math.min(scene.width * 0.32, scene.height * 0.46);
  return constrainImageElement({
    ...TOOLTIP_SAMPLE, id: `tooltip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: "image", label: "Tooltip", x: scene.width * 0.5, y: scene.height * 0.5,
    width, height: width * layout.height / layout.width, baseWidth: width, rotation: 0, z: topZ + 1,
    shadow: { enabled: true, color: "#000000", opacity: 0.55, blur: 12, offsetX: 2, offsetY: 7 },
  }, scene);
};

export const reflowTooltip = (element) => {
  const layout = getTooltipLayout(element);
  return { ...element, height: element.width * layout.height / layout.width };
};

export const paintTooltipCanvas = (canvas, element, assets, density = 1) => {
  const layout = getTooltipLayout(element);
  const width = element.width || layout.width, height = element.height || layout.height;
  const ratio = Math.min(density, 8192 / Math.max(width, height), Math.sqrt(16000000 / (width * height)));
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const context = canvas.getContext("2d");
  if (!context) return canvas;
  context.scale(canvas.width / layout.width, canvas.height / layout.height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  // Only stretch the middle slice; the authored bevels keep their proportions.
  const top = layout.width * 62 / 1000, bottom = layout.width * 64 / 1000;
  context.drawImage(assets.frame, 0, 0, 1000, 62, 0, 0, layout.width, top);
  context.drawImage(assets.frame, 0, 62, 1000, 248, 0, top, layout.width, layout.height - top - bottom);
  context.drawImage(assets.frame, 0, 310, 1000, 64, 0, layout.height - bottom, layout.width, bottom);
  // Text is rasterized from TTF outlines at the output's actual pixel density.
  context.textAlign = "left";
  context.textBaseline = "top";
  context.lineJoin = "round";
  context.fillStyle = "#ffffff";
  context.strokeStyle = "#000000";
  context.lineWidth = 2.2;
  context.font = `400 ${layout.titleSize}px ${TOOLTIP_TITLE_FONT}`;
  layout.titleLines.forEach((line, index) => {
    const y = layout.titleY + index * layout.titleAdvance;
    context.strokeText(line, layout.padding, y);
    context.fillText(line, layout.padding, y);
  });
  context.font = `400 ${layout.bodySize}px ${TOOLTIP_BODY_FONT}`;
  context.shadowColor = "#00000080";
  context.shadowBlur = 0;
  context.shadowOffsetY = canvas.height / layout.height * 0.7;
  layout.bodyLines.forEach((line, index) => context.fillText(line, layout.padding, layout.bodyY + index * layout.bodyAdvance));
  return canvas;
};
